import React from 'react'
import MDEditor, { commands } from '@uiw/react-md-editor'
import rehypeSanitize from 'rehype-sanitize'
import { Box, BoxProps, Typography } from '@mui/material'
import { Controller, RegisterOptions, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { themeIcons } from '@theme/icons'

import { HeadlineExtension } from './mdExtensions/HeadlineExtension'

type Props = {
  name: string,
  width?: string | number,
  height?: number,
  max?: number,
  options?: RegisterOptions
  style?: BoxProps['style']
  previewLabel?: boolean
}

const extraCommands = [
  commands.codeEdit,
  commands.codeLive,
  commands.codePreview
]

export const MarkdownEditor: React.FC<Props> = ({ name, options, width, height = 300, style, max, previewLabel }) => {
  const { t } = useTranslation()
  const { control } = useFormContext()

  const editorCommands = React.useMemo(() => [
    {
      name: 'bold',
      keyCommand: 'bold',
      buttonProps: { 'aria-label': t('common.bold') },
      icon: <themeIcons.FormatBold />,
      execute: (state: commands.TextState, api: commands.TextAreaTextApi) => {
        let modifyText = `**${state.selectedText}**`
        const { selection } = state

        if (!state.selectedText) {
          modifyText = '****'
        }
        api.replaceSelection(modifyText)

        if (!state.selectedText) {
          selection.start += 2
          selection.end += 2
          api.setSelectionRange(selection)
        }
      }
    },
    {
      name: 'italic',
      keyCommand: 'italic',
      buttonProps: { 'aria-label': t('common.italic') },
      icon: <themeIcons.FormatItalic />,
      execute: (state: commands.TextState, api: commands.TextAreaTextApi) => {
        let modifyText = `*${state.selectedText}*`
        const { selection } = state

        if (!state.selectedText) {
          modifyText = '**'
        }
        api.replaceSelection(modifyText)

        if (!state.selectedText) {
          selection.start += 1
          selection.end += 1
          api.setSelectionRange(selection)
        }
      }
    },
    commands.group([commands.title1, commands.title2, commands.title3], {
      name: 'title',
      groupName: 'title',
      buttonProps: { 'aria-label': t('common.insertTitle') },
      icon: <themeIcons.FormatHeading />,
      children: (handle) => (<HeadlineExtension {...handle} />)
    }),
    commands.quote,
    commands.divider,
    commands.unorderedListCommand,
    commands.orderedListCommand,
    commands.divider,
    {
      name: 'help',
      keyCommand: 'help',
      buttonProps: { 'aria-label': t('common.help') },
      icon: <themeIcons.Help sx={{
        '&.MuiSvgIcon-root': {
          '&.MuiSvgIcon-root': {
            p: '4px'
          }
        }
      }}
      />,
      execute: () => {
        window.open('https://www.markdownguide.org/basic-syntax/', '_blank')
      }
    }
  ], [])

  return (
    <Box width={width}
      style={style}
      sx={{
        ...(previewLabel && {
          '.w-md-editor-preview': {
            paddingTop: '60px!important',
            '&::before': {
              content: `"${t('common.preview')}"`,
              position: 'absolute',
              top: '14px',
              fontFamily: 'Signika',
              fontSize: '18px',
              fontWeight: 'bold'
            }
          }
        })
      }}
    >
      <Controller
        name={name}
        control={control}
        rules={options}
        render={({ field: { value, onChange } }) => (
          <>
            <MDEditor
              value={value}
              onChange={onChange}
              height={height}
              commands={editorCommands}
              extraCommands={extraCommands}
              textareaProps={{
                maxLength: max
              }}
              previewOptions={{
                rehypePlugins: [[rehypeSanitize]]
              }}
            />

            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end'
            }}
            >
              {max && <Typography variant="subtitle1"
                sx={{
                  mt: 0.5,
                  color: 'grey.700'
                }}
              >
                {t('common.charactersRemaining', { count: max - (value ? value.length : 0) })}
              </Typography>}
            </Box>
          </>)}
      ></Controller>
    </Box>
  )
}
